// Student homepage — canonical design: design repo docs/designs/2026-07-23-student-webapp-demo/
// rrid-homepage.dc.html ("RRID Homepage" Claude Design import, 2026-07-28). The design replaces
// the transcript-era layout (KPI cards + My Preprints columns): one "Waiting on you" list
// (Fixable → COI → the student's preprints, per reviewerAttentionRank), the assignment-slot chip,
// the embedded team calendar, and a public-site footer link. Historical numbers live in
// My Productivity; the homepage is the live snapshot.
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { clsx } from "clsx";
import { MINI_TEAM_QUEUE_JUMP_GREENLIT, type StudentDashboard } from "@rrid/shared-types";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { useAuth } from "../../lib/auth.js";
import { Spinner } from "../../components/ui.js";
import { TEAM_CALENDAR_OPEN, TeamCalendarIframe } from "./TeamCalendar.js";

/** Deep-link into My Quality Checks focused on Fixable rows that need the student fix loop. */
const QC_ATTENTION_HREF = "/quality-check?attention=1";

const PUBLIC_SITE_HREF = "https://rrid.mitpress.mit.edu";

type RowTone = "red" | "slate" | "blue" | "amber" | "violet" | "teal";

const BAR: Record<RowTone, string> = {
  red: "bg-red-700",
  slate: "bg-slate-400",
  blue: "bg-blue-500",
  amber: "bg-amber-500",
  violet: "bg-violet-700",
  teal: "bg-teal-700",
};
const CHIP: Record<RowTone, string> = {
  red: "bg-red-50 text-red-800",
  slate: "bg-slate-100 text-slate-600",
  blue: "bg-blue-100 text-blue-800",
  amber: "bg-amber-100 text-amber-800",
  violet: "bg-violet-100 text-violet-700",
  teal: "bg-teal-50 text-teal-800",
};
const BUTTON: Record<string, string> = {
  red: "bg-red-700 text-white hover:bg-red-800",
  redTint: "bg-red-50 text-red-800 hover:bg-red-100",
  violet: "bg-violet-700 text-white hover:bg-violet-800",
  teal: "bg-teal-700 text-white hover:bg-teal-800",
  quiet: "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
};

interface AttentionItem {
  key: string;
  tone: RowTone;
  chip: string;
  title: string;
  sub: string;
  expiring?: boolean;
  action: { label: string; to: string; style: keyof typeof BUTTON };
}

const COUNT_WORDS = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"];

function waitingLine(n: number): string {
  if (n === 0) return "Nothing is waiting on you today.";
  if (n === 1) return "One thing is waiting on you today.";
  return `${n < COUNT_WORDS.length ? COUNT_WORDS[n] : n} things are waiting on you today.`;
}

function daysLeft(expiresAt: string | null | undefined): string | null {
  const t = expiresAt ? Date.parse(expiresAt) : NaN;
  if (!Number.isFinite(t)) return null;
  const d = Math.max(0, Math.ceil((t - Date.now()) / 86_400_000));
  return `${d} day${d === 1 ? "" : "s"} left`;
}

function counts(c: { proposedCount?: number; greenlitCount?: number }): string {
  return `${c.proposedCount ?? 0} proposed · ${c.greenlitCount ?? 0} greenlit`;
}

function AttentionRow({ item }: { item: AttentionItem }): JSX.Element {
  return (
    <div className="flex items-center gap-4 border-b border-slate-100 px-5 py-4 last:border-b-0 hover:bg-slate-50">
      <span className={clsx("w-1 shrink-0 self-stretch rounded-full", BAR[item.tone])} />
      <span className={clsx("w-32 shrink-0 rounded px-2 py-1 text-center text-[11px] font-bold tracking-wide", CHIP[item.tone])}>
        {item.chip}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2 text-[15px] font-medium leading-snug">
          {item.title}
          {item.expiring && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded bg-amber-50 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-800">
              ⚠️ Expiring soon
            </span>
          )}
        </span>
        <span className="mt-0.5 block text-[13px] text-slate-500">{item.sub}</span>
      </span>
      <Link
        to={item.action.to}
        className={clsx("shrink-0 rounded-lg px-4 py-2 text-[13px] font-semibold", BUTTON[item.action.style])}
      >
        {item.action.label}
      </Link>
    </div>
  );
}

export function StudentDashboardView(): JSX.Element {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: qk.dashboardStudent,
    queryFn: () => api.get<StudentDashboard>("/dashboard/student"),
  });
  const firstName = user?.name?.split(" ")[0] || "there";

  if (isLoading || !data) return <Spinner label="Loading dashboard…" />;
  const k = data.kpis;
  const p = data.preprints;

  // Mini-team preprints render as one band under Active (WO-7/23 Track A): the ACTIVE ones the
  // server already flags, plus the pre-activation (Dormant/On Hold) `miniTeam` rows.
  const activePlain = p.activeForMe.filter((c) => !c.isMiniTeam);
  const activeMini = [
    ...p.activeForMe.filter((c) => c.isMiniTeam),
    ...p.miniTeam.filter((m) => !p.activeForMe.some((a) => a.preprintId === m.preprintId)),
  ];

  const detailsHref = (c: { preprintId: string | null }): string =>
    c.preprintId ? `/preprints/${c.preprintId}` : "/preprints";
  const proposeHref = (c: { preprintId: string | null }): string =>
    c.preprintId ? `/reviewers?preprint=${c.preprintId}` : "/reviewers";

  const peerFix: AttentionItem[] = [
    ...data.reviewers
      .filter((r) => r.status === "Fixable")
      .map((r): AttentionItem => ({
        key: `fix-${r.id}`,
        tone: "red",
        chip: "Fixable",
        title: `${r.name} — ${r.preprintTitle}`,
        sub: "An AE left a comment on your proposal · edit the profile, then Mark as Fixed",
        action: { label: "Edit & mark Fixed", to: QC_ATTENTION_HREF, style: "red" },
      })),
    ...data.reviewers
      .filter((r) => r.status === "COI Detected" && !r.coiContested)
      .map((r): AttentionItem => ({
        key: `coi-${r.id}`,
        tone: "red",
        chip: "COI Detected",
        title: `${r.name} — ${r.preprintTitle}`,
        sub: "Flagged by the COI check · contest with a justification or propose someone else",
        action: { label: "Review COI", to: proposeHref(r), style: "redTint" },
      })),
  ];

  const peerFind: AttentionItem[] = [
    ...activePlain.map((c): AttentionItem => ({
      key: `act-${c.preprintId ?? c.title}`,
      tone: "violet",
      chip: "Active",
      title: c.title,
      sub: counts(c),
      action: { label: "Propose reviewers", to: proposeHref(c), style: "violet" },
    })),
    ...activeMini.map((c): AttentionItem => {
      const expiring = c.miniTeamPhase === "expiring";
      const left = expiring ? daysLeft(c.miniTeamExpiresAt) : null;
      return {
        key: `mini-${c.preprintId ?? c.title}`,
        tone: "teal",
        chip: "Active · Mini team",
        title: c.title,
        expiring,
        sub: [
          "You're on the mini team",
          counts(c),
          left ? `${left} to reach ${MINI_TEAM_QUEUE_JUMP_GREENLIT} greenlit reviewers` : null,
        ]
          .filter(Boolean)
          .join(" · "),
        action: { label: "Propose reviewers", to: proposeHref(c), style: "teal" },
      };
    }),
  ];

  const pitchesToComplete: AttentionItem[] = p.claimed.map((c): AttentionItem => ({
    key: `taken-${c.preprintId ?? c.title}`,
    tone: "slate",
    chip: "Taken",
    title: c.title,
    sub: `Holds one of your ${k.slotsCap} assignment slots until you pitch it`,
    action: { label: "Complete pitch", to: detailsHref(c), style: "violet" },
  }));

  // Stefano: peer-reviewer work grouped; pitches at the bottom of Waiting on you.
  const sections: { key: string; title: string; items: AttentionItem[] }[] = [
    { key: "fix", title: "Peer reviewers to fix", items: peerFix },
    { key: "find", title: "Peer reviewers still to find", items: peerFind },
    { key: "pitch", title: "Pitches to complete", items: pitchesToComplete },
  ].filter((s) => s.items.length > 0);

  const items = sections.flatMap((s) => s.items);

  // Queued stages — nothing is waiting on the student here (the team pitch happens in the meeting,
  // editorial is the AE's, Selected waits on activation), so they sit below the attention list
  // rather than inflating it. They carried the old "My Preprints" columns' information.
  const inFlight: { key: string; chip: string; tone: RowTone; title: string; to: string }[] = [
    ...p.toPitchTeam.map((c) => ({
      key: `tpt-${c.preprintId ?? c.title}`,
      chip: "To Pitch(Team)",
      tone: "blue" as RowTone,
      title: c.title,
      to: detailsHref(c),
    })),
    ...p.toPitchEditorial.map((c) => ({
      key: `tpe-${c.preprintId ?? c.title}`,
      chip: "To Pitch(Editorial)",
      tone: "amber" as RowTone,
      title: c.title,
      to: detailsHref(c),
    })),
    ...p.inPipeline.map((c) => ({
      key: `sel-${c.preprintId ?? c.title}`,
      chip: "Selected",
      tone: "amber" as RowTone,
      title: c.title,
      to: detailsHref(c),
    })),
  ];

  const today = new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long" }).format(new Date());

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Good day, {firstName}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <p className="text-sm text-slate-500">{waitingLine(items.length)}</p>
            <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-800">
              Assignment slots {k.slotsUsed} of {k.slotsCap}
              <span className="flex gap-1">
                {Array.from({ length: k.slotsCap }, (_, i) => (
                  <span key={i} className={clsx("h-1.5 w-1.5 rounded-full", i < k.slotsUsed ? "bg-blue-700" : "bg-blue-200")} />
                ))}
              </span>
            </span>
          </div>
        </div>
        <div className="text-[13px] text-slate-400">{today} · updated just now</div>
      </header>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <h2 className="text-[17px] font-bold tracking-tight">Waiting on you</h2>
          <span className="text-[13px] text-slate-400">
            Peer reviewers to fix · still to find · pitches to complete
          </span>
        </div>
        {items.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-slate-500">
            Nothing is waiting on you.{" "}
            <Link to="/preprints" className="font-medium text-violet-700 hover:underline">
              Claim a preprint to get started →
            </Link>
          </div>
        ) : (
          sections.map((section) => (
            <div key={section.key}>
              <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-2">
                <h3 className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                  {section.title}
                </h3>
              </div>
              {section.items.map((item) => (
                <AttentionRow key={item.key} item={item} />
              ))}
            </div>
          ))
        )}
      </section>

      {inFlight.length > 0 && (
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
            <h2 className="text-[17px] font-bold tracking-tight">Also in flight</h2>
            <span className="text-[13px] text-slate-400">Queued — no action needed from you yet</span>
          </div>
          {inFlight.map((c) => (
            <div key={c.key} className="flex items-center gap-4 border-b border-slate-100 px-5 py-3 last:border-b-0 hover:bg-slate-50">
              <span className={clsx("w-32 shrink-0 rounded px-2 py-1 text-center text-[11px] font-bold tracking-wide", CHIP[c.tone])}>
                {c.chip}
              </span>
              <Link to={c.to} className="min-w-0 flex-1 text-sm text-slate-700 hover:text-violet-700 hover:underline">
                {c.title}
              </Link>
            </div>
          ))}
        </section>
      )}

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <h2 className="text-[17px] font-bold tracking-tight">Team calendar</h2>
          <a
            href={TEAM_CALENDAR_OPEN}
            target="_blank"
            rel="noopener"
            className="text-[13px] font-semibold text-violet-700 hover:text-violet-900 hover:underline"
          >
            Open in Google Calendar ↗
          </a>
        </div>
        <TeamCalendarIframe heightClass="h-[560px]" />
      </section>

      <div className="flex justify-end border-t border-slate-200 pt-4">
        <a
          href={PUBLIC_SITE_HREF}
          target="_blank"
          rel="noopener"
          className="text-sm font-semibold text-violet-700 hover:text-violet-900 hover:underline"
        >
          See the public RR\ID site ↗
        </a>
      </div>
    </div>
  );
}
